/* A Leading Role - close the cart drawer and refresh the bag after checkout.
 *
 * The problem this solves: alr.js opens /checkout in a popup window
 * (openCheckoutPopup) and, when that popup closes, calls renderBag() but never
 * closeCartDrawer(). So a customer who has just paid comes back to a drawer
 * still sitting open. Three ways it goes wrong today:
 *
 *   1. popup closes after the order  -> drawer stays open on the empty state
 *   2. popup is blocked (most phones) -> this tab navigates to /checkout, and
 *      Back restores the page from bfcache with <dialog open> and the old
 *      lines still in #mbag-root. DOMContentLoaded does not fire on a bfcache
 *      restore, so nothing in alr.js re-runs at all.
 *   3. customer clicks "Continue shopping" inside the popup -> the popup never
 *      closes, so alr.js's popup.closed poll never fires and the drawer sits
 *      open with the old contents indefinitely.
 *
 * Additive on purpose: alr.js is a 63KB IIFE that other work edits, and none
 * of openBagDrawer / closeCartDrawer / renderBag / fetchCart is reachable from
 * outside it. So this drives the drawer through the DOM instead:
 *   - clicking #mbag-keep-shopping runs alr.js's own close path, which gives
 *     the slide-out transition AND fires the native `close` event that alr.js
 *     listens for to release the scroll lock
 *   - #bag-count is plain text, so the badge can be set directly
 *   - reopening the drawer makes alr.js call renderBag() with no argument,
 *     which self-fetches /cart.js - so there is no need to duplicate its
 *     markup here, only to get the stale drawer shut.
 *
 * Entirely fail-safe: every fetch and storage call is wrapped, and if anything
 * is missing this does nothing and leaves today's behaviour untouched.
 */
(function () {
  var KEY = 'alr_checkout_started';
  var MAX_AGE_MS = 3 * 60 * 60 * 1000; // a checkout older than this is not ours

  function markStarted() {
    try { sessionStorage.setItem(KEY, String(Date.now())); } catch (err) {}
  }

  function startedRecently() {
    try {
      var t = Number(sessionStorage.getItem(KEY) || 0);
      return !!t && Date.now() - t < MAX_AGE_MS;
    } catch (err) {
      return false;
    }
  }

  function clearStarted() {
    try { sessionStorage.removeItem(KEY); } catch (err) {}
  }

  function drawer() {
    return document.getElementById('modal-bag');
  }

  function isOpen() {
    var d = drawer();
    return !!(d && d.hasAttribute('open'));
  }

  function setBadge(count) {
    var b = document.getElementById('bag-count');
    if (!b) return;
    b.textContent = count;
    b.classList.toggle('show', count > 0);
  }

  function closeDrawer() {
    var d = drawer();
    if (!d || !d.hasAttribute('open')) return;

    // Preferred path: alr.js's own handler, so the transition and its
    // scroll-lock release both happen exactly as they normally would.
    var keep = document.getElementById('mbag-keep-shopping');
    if (keep) {
      keep.click();
    } else {
      // Empty-state markup has no Keep Shopping button. close() still fires
      // the native `close` event alr.js listens for, so the lock is released.
      d.classList.remove('show');
      try { d.close(); } catch (err) {}
    }

    // Only if something went wrong and the body is still pinned. Deliberately
    // does not scrollTo - alr.js already restores the position when it can,
    // and doing it twice makes the page jump.
    setTimeout(function () {
      if (document.body.classList.contains('modal-open') && !isOpen()) {
        document.body.classList.remove('modal-open');
        document.body.style.top = '';
      }
    }, 600);
  }

  // force = true: shut the drawer whatever the cart says (used on a history
  // restore, where anything still on screen was rendered before the customer
  // left the page and cannot be trusted).
  function refresh(force) {
    if (!force && !startedRecently()) return;

    fetch('/cart.js', { credentials: 'same-origin', cache: 'no-store' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        if (!cart) return;
        setBadge(cart.item_count);

        if (cart.item_count === 0) {
          // Checkout completed (or the bag was emptied while we were away).
          clearStarted();
          if (isOpen()) closeDrawer();
        } else if (force && isOpen()) {
          // Came back through history with the drawer still up. Shut it; the
          // next open re-renders from /cart.js on its own.
          closeDrawer();
        }
      })
      .catch(function () {});
  }

  // Capture phase: alr.js calls preventDefault on this click, but capture runs
  // before its listener, so the flag is set either way.
  document.addEventListener(
    'click',
    function (e) {
      var t = e.target;
      if (t && t.closest && t.closest('#mbag-checkout')) markStarted();
    },
    true
  );

  // Returning to the tab after the checkout popup - covers the case where the
  // popup is left open, which alr.js's popup.closed poll never catches.
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible') refresh(false);
  });
  window.addEventListener('focus', function () { refresh(false); });

  // Back/forward. On a bfcache restore nothing else in the theme re-runs.
  window.addEventListener('pageshow', function (e) {
    var restored = !!e.persisted;
    if (!restored) {
      try {
        var nav = performance.getEntriesByType('navigation')[0];
        restored = !!nav && nav.type === 'back_forward';
      } catch (err) {}
    }
    if (restored) refresh(true);
  });
})();
